import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { quizAPI } from '../utils/api';
import Button from '../components/Button';

interface Quiz {
  _id: string;
  title: string;
  description: string;
  category: string;
  difficulty: string;
  questionsCount: number;
  timeLimit?: number;
  createdAt: string;
  active?: boolean;
}

const QuizListPage: React.FC = () => {
  const [quizzes, setQuizzes] = useState<Quiz[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [selectedCategory, setSelectedCategory] = useState<string>('');
  const [selectedDifficulty, setSelectedDifficulty] = useState<string>('');
  const [categories, setCategories] = useState<string[]>([]);
  const difficulties = ['Let', 'Mellem', 'Svær'];

  useEffect(() => {
    const fetchQuizzes = async () => {
      try {
        setLoading(true);
        const response = await quizAPI.getAllQuizzes(currentPage, 9);
        
        // Check if quizzes array exists in the response
        const quizzesData = response.data.quizzes || response.data || [];
        
        // Always filter out inactive quizzes on the public page, even for admins
        const activeQuizzes = quizzesData.filter((quiz: Quiz) => quiz.active !== false);
        setQuizzes(activeQuizzes);
        
        // Set total pages safely
        setTotalPages(response.data.totalPages || 1);
        
        // Extract unique categories safely
        if (activeQuizzes && activeQuizzes.length > 0) {
          const uniqueCategories = Array.from(
            new Set(activeQuizzes.map((quiz: Quiz) => quiz.category).filter(Boolean))
          );
          setCategories(uniqueCategories as string[]);
        }
      } catch (err: any) {
        setError(err.response?.data?.message || 'Kunne ikke indlæse quizzer');
      } finally {
        setLoading(false);
      }
    };

    fetchQuizzes();
  }, [currentPage]);

  const handlePageChange = (page: number) => {
    setCurrentPage(page);
  };

  const handleCategoryChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    setSelectedCategory(e.target.value);
  };

  const handleDifficultyChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    setSelectedDifficulty(e.target.value);
  };

  // Add a safe check for quizzes before filtering
  const filteredQuizzes = quizzes && quizzes.length > 0
    ? quizzes
        .filter(quiz => !selectedCategory || quiz.category === selectedCategory)
        .filter(quiz => !selectedDifficulty || quiz.difficulty === selectedDifficulty)
    : [];

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('da-DK', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
    });
  };

  if (loading && quizzes.length === 0) {
    return (
      <div className="container-custom py-12 text-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-snudefryd-blue mx-auto"></div>
        <p className="mt-4 text-gray-600">Indlæser quizzer...</p>
      </div>
    );
  }

  if (error) {
    return (
      <div className="container-custom py-12">
        <div className="bg-red-50 border border-red-200 rounded-lg p-6 text-center">
          <h3 className="text-xl font-bold text-red-700 mb-4">Ups!</h3>
          <p className="text-red-600 mb-6">{error}</p>
          <Button onClick={() => window.location.reload()} variant="primary">
            Prøv igen
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div className="bg-snudefryd-beige min-h-screen py-12">
      <div className="container-custom">
        {/* Header */}
        <div className="text-center mb-12">
          <h1 className="text-3xl md:text-4xl font-bold mb-4">Hundeernærings Quizzer</h1>
          <p className="text-lg text-gray-700 max-w-2xl mx-auto">
            Test din viden om hundeernæring med vores interaktive quizzer. Lær mere om hvad der er bedst for din hunds sundhed og velvære.
          </p>
        </div>

        {/* Filters */}
        <div className="bg-white rounded-lg shadow-md p-6 mb-8">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label htmlFor="category" className="block text-sm font-medium text-gray-700 mb-1">
                Kategori
              </label>
              <select
                id="category"
                value={selectedCategory}
                onChange={handleCategoryChange}
                className="w-full p-2 border border-gray-300 rounded-md focus:ring-snudefryd-blue focus:border-snudefryd-blue"
              >
                <option value="">Alle kategorier</option>
                {categories.map((category) => (
                  <option key={category} value={category}>
                    {category}
                  </option>
                ))}
              </select>
            </div>
            <div>
              <label htmlFor="difficulty" className="block text-sm font-medium text-gray-700 mb-1">
                Sværhedsgrad
              </label>
              <select
                id="difficulty"
                value={selectedDifficulty}
                onChange={handleDifficultyChange}
                className="w-full p-2 border border-gray-300 rounded-md focus:ring-snudefryd-blue focus:border-snudefryd-blue"
              >
                <option value="">Alle sværhedsgrader</option>
                {difficulties.map((difficulty) => (
                  <option key={difficulty} value={difficulty}>
                    {difficulty}
                  </option>
                ))}
              </select>
            </div>
          </div>
        </div>

        {/* Quiz Grid */}
        {filteredQuizzes.length === 0 ? (
          <div className="bg-white rounded-lg shadow-md p-8 text-center">
            <h3 className="text-xl font-bold text-gray-700 mb-4">Ingen quizzer fundet</h3>
            <p className="text-gray-600 mb-6">
              Der er ingen quizzer, der matcher dine filtre. Prøv at ændre dine filtre eller kom tilbage senere.
            </p>
            <Button onClick={() => {
              setSelectedCategory('');
              setSelectedDifficulty('');
            }} variant="primary">
              Nulstil filtre
            </Button>
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {filteredQuizzes.map((quiz) => (
              <div key={quiz._id} className="bg-white rounded-lg shadow-md overflow-hidden transition-transform hover:transform hover:scale-105">
                <div className={`h-2 ${
                  quiz.difficulty === 'Let' ? 'bg-green-500' :
                  quiz.difficulty === 'Mellem' ? 'bg-yellow-500' : 'bg-red-500'
                }`}></div>
                <div className="p-6">
                  <div className="flex justify-between items-start mb-4">
                    <h3 className="text-xl font-bold">{quiz.title}</h3>
                    <span className={`text-xs font-medium px-2 py-1 rounded-full ${
                      quiz.difficulty === 'Let' ? 'bg-green-100 text-green-800' :
                      quiz.difficulty === 'Mellem' ? 'bg-yellow-100 text-yellow-800' : 'bg-red-100 text-red-800'
                    }`}>
                      {quiz.difficulty}
                    </span>
                  </div>
                  <p className="text-gray-600 mb-4 line-clamp-2">{quiz.description}</p>
                  <div className="flex flex-wrap gap-2 mb-4">
                    <span className="bg-snudefryd-light-purple text-snudefryd-blue px-2 py-1 rounded-full text-xs">
                      {quiz.category}
                    </span>
                    <span className="bg-gray-100 text-gray-700 px-2 py-1 rounded-full text-xs">
                      {quiz.questionsCount} spørgsmål
                    </span>
                    {quiz.timeLimit && (
                      <span className="bg-gray-100 text-gray-700 px-2 py-1 rounded-full text-xs">
                        {quiz.timeLimit} min
                      </span>
                    )}
                  </div>
                  <div className="flex justify-between items-center">
                    <span className="text-xs text-gray-500">
                      Oprettet: {formatDate(quiz.createdAt)}
                    </span>
                    <Link to={`/quiz/${quiz._id}`}>
                      <Button variant="primary" size="sm">
                        Start Quiz
                      </Button>
                    </Link>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}

        {/* Pagination */}
        {totalPages > 1 && (
          <div className="flex justify-center mt-8">
            <div className="flex space-x-2">
              <Button
                onClick={() => handlePageChange(currentPage - 1)}
                variant="outline"
                disabled={currentPage === 1}
              >
                Forrige
              </Button>
              {Array.from({ length: totalPages }, (_, i) => i + 1).map((page) => (
                <button
                  key={page}
                  onClick={() => handlePageChange(page)}
                  className={`w-10 h-10 rounded-md ${
                    currentPage === page
                      ? 'bg-snudefryd-blue text-white'
                      : 'bg-white text-gray-700 hover:bg-gray-100'
                  }`}
                >
                  {page}
                </button>
              ))}
              <Button
                onClick={() => handlePageChange(currentPage + 1)}
                variant="outline"
                disabled={currentPage === totalPages}
              >
                Næste
              </Button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default QuizListPage; 
